<template>
  <div>
    <PageContainer :class="$style.pageContainer">
      <template v-slot:body>
        <Container>
          <Card
            :hasFooterDivider="false"
            :hasHeaderDivider="false"
          >
            <template v-slot:header>
              <HeadingMedium>
                <ToggleSwitch
                  :key="toggleKey"
                  :class="$style.mdmToggle"
                  label="Allow users to enroll devices into Windows MDM through the User Portal"
                  :value="isMdmEnabled"
                  @input="handleToggle"
                />
              </HeadingMedium>
            </template>
          </Card>
        </Container>
      </template>
    </PageContainer>
    <SeverityModal
      notificationTitle="Enabling this toggle will alter global User Portal Settings for all users."
      :primaryButtonProps="primaryButtonProps"
      :showModal="showEnableUserPortalEnrollModal"
      title="Allow users to enroll devices into Windows MDM through the User Portal"
      variant="sev2"
      @cancel="handleCancelMdmToggle"
      @save="handleConfirmMdmToggle"
    >
      <template v-slot:body>
        <ParagraphBody>
          <div>
            Enabling this toggle will allow users to enroll macOS, Windows,
            or Linux devices through the user portal.
          </div>
        </ParagraphBody>
      </template>
      <template v-slot:learnmore>
        <ParagraphBody>
          For more information, see
          <Link
            href="https://support.jumpcloud.com/s/article/Enabling-Users-to-Install-the-JumpCloud-Agent-from-the-User-Portal"
            target="_blank"
            text="Enabling Users to Install the JumpCloud Agent from the User Portal"
          />
        </ParagraphBody>
      </template>
    </SeverityModal>
  </div>
</template>

<script>

import {
  Card, Container, HeadingMedium, Link, PageContainer, ParagraphBody,
  SeverityModal, ToggleSwitch, Types,
} from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';

const { PrimaryButtonProps } = Types;

export default {
  name: 'MdmWindowsSettings',

  components: {
    Card,
    PageContainer,
    ParagraphBody,
    Container,
    HeadingMedium,
    ToggleSwitch,
    SeverityModal,
    Link,
  },

  data: () => ({
    toggleKey: 0,
    isMdmEnabled: false,
    showEnableUserPortalEnrollModal: false,
  }),

  computed: {
    ...mapState('SettingsModel', [
      'item',
    ]),

    primaryButtonProps() {
      return PrimaryButtonProps({
        isLoading: false,
        isDisabled: false,
        hasOutline: false,
        text: 'Continue',
        type: 'primary',
      });
    },
  },

  async mounted() {
    await this.loadWindowsMdmSettings();
  },

  methods: {

    ...mapActions('SettingsModel', [
      'fetchItem',
      'saveItem',
      'updateItem',
    ]),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    handleCancelMdmToggle() {
      this.showEnableUserPortalEnrollModal = false;
      this.toggleKey += 1;
    },

    handleConfirmMdmToggle() {
      this.saveOrgSettings();
      this.showEnableUserPortalEnrollModal = false;
    },

    handleToggle() {
      const isUserPortalEnrollEnabled = get(this.item, 'enableUserPortalAgentInstall');
      if (!isUserPortalEnrollEnabled && !this.isMdmEnabled) {
        this.showEnableUserPortalEnrollModal = true;
      } else {
        this.saveOrgSettings();
      }
    },

    async loadWindowsMdmSettings() {
      await this.fetchItem();
      this.isMdmEnabled = get(this.item, 'windowsMDM.enabled');
      this.toggleKey += 1;
    },

    saveOrgSettings() {
      const isUserPortalEnrollEnabled = get(this.item, 'enableUserPortalAgentInstall');

      const updatedMdmValue = !this.isMdmEnabled;
      const notificationStatus = updatedMdmValue ? 'enabled' : 'disabled';

      const updatedItemValues = !isUserPortalEnrollEnabled && !this.isMdmEnabled
        ? { enableUserPortalAgentInstall: true, windowsMDM: { enabled: updatedMdmValue } }
        : { windowsMDM: { enabled: updatedMdmValue } };

      this.updateItem({ ...this.item, ...updatedItemValues });
      this.saveItem()
        .then(() => {
          this.triggerSuccessNotification({
            title: 'Saved',
            message: `Windows MDM has been ${notificationStatus}.`,
          });
          this.isMdmEnabled = updatedMdmValue;
        })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to update MDM status.');
          this.triggerErrorNotification({ title: 'Error', message });
        });
    },
  },

};
</script>

<style module>
.pageContainer {
  background: var(--jc-background);
}

.mdmToggle {
  --switch-label-weight: 600;
}
</style>
